import {
  CustomTaskRepetition,
  EquipmentData,
  isCustomWeeklyRepetition,
  User
} from '@hconnect/common/types'
import {
  attachmentFilesToFormData,
  appendDateData,
  appendAttachmentsToDelete
} from '@hconnect/common/utils'

import {
  ProcessWorkOrderAttachment,
  ProcessWorkOrderCreateDto,
  ProcessWorkOrderEditDto
} from '../types'

export const prepareProcessWorkOrderFormData = async (
  event: ProcessWorkOrderCreateDto | ProcessWorkOrderEditDto,
  skipKeys: string[] = []
): Promise<FormData> => {
  const formData = new FormData()
  const keys = Object.keys(event).filter((item) => skipKeys.indexOf(item) < 0)
  for (const key of keys) {
    const value = event[key]
    if (value) {
      switch (key) {
        case 'attachments':
          await attachmentFilesToFormData(value, formData)
          break
        case 'dueDate':
        case 'doNotStartBefore':
          appendDateData(formData, value as Date, key)
          break
        case 'equipment':
          appendEquipmentData(formData, value as EquipmentData)
          break
        case 'mainEquipment':
          appendEquipmentData(formData, value as EquipmentData, true)
          break
        case 'attachmentsToDelete':
          appendAttachmentsToDelete(formData, value as string[])
          break
        case 'assignees':
          appendAssignees(formData, value as User[] | undefined)
          break
        case 'repetitionInfo':
          appendRepetitionInfo(formData, value as CustomTaskRepetition)
          break
        case 'checklistTemplateIds':
          appendChecklistTemplateIds(formData, value as string[])
          break
        default:
          formData.append(key, value as string | Blob)
      }
    }
  }
  return formData
}

export const appendChecklistTemplateIds = (formData: FormData, checklistTemplateIds: string[]) => {
  checklistTemplateIds.forEach((id, index) => formData.append(`checklistTemplateIds[${index}]`, id))
}

export const appendRepetitionInfo = (formData: FormData, repetitionInfo: CustomTaskRepetition) => {
  repetitionInfo.repetition
    ? formData.append('repetitionInfo.repetition', repetitionInfo.repetition)
    : null
  repetitionInfo.repeatEvery
    ? formData.append('repetitionInfo.repeatEvery', repetitionInfo.repeatEvery.toString())
    : null
  repetitionInfo.endDate
    ? appendDateData(formData, repetitionInfo.endDate, 'repetitionInfo.endDate')
    : null
  repetitionInfo.endsAfterRepetitions
    ? formData.append(
        'repetitionInfo.endsAfterRepetitions',
        repetitionInfo.endsAfterRepetitions.toString()
      )
    : null
  appendWeeklyRepetitionWeekDays(formData, repetitionInfo)
}

export const appendAssignees = (formData: FormData, assignees: User[] | undefined) => {
  if (Array.isArray(assignees)) {
    assignees.forEach((user: User) => {
      formData.append('assignees', user.id)
    })
  }
}

export const appendWeeklyRepetitionWeekDays = (
  formData: FormData,
  repetitionInfo: CustomTaskRepetition
) => {
  if (isCustomWeeklyRepetition(repetitionInfo)) {
    repetitionInfo.weekDays.forEach((day, index) =>
      formData.append(`repetitionInfo.weekDays[${index}]`, String(day))
    )
    return
  }
}

export const appendEquipmentData = (
  formData: FormData,
  equipment: EquipmentData,
  mainEquipment?: boolean
) => {
  const equipmentKey = mainEquipment ? 'mainEquipment' : 'equipment'
  formData.append(`${equipmentKey}Id`, equipment.id)
  formData.append(`${equipmentKey}IdType`, equipment.idType as string)
  formData.append(`${equipmentKey}Text`, equipment.text as string)
}

export const convertAttachmentsToFiles = async (
  attachments: File[] | ProcessWorkOrderAttachment[]
): Promise<File[]> => {
  const files = await Promise.all(
    attachments.map(async (attachment) => {
      const extAttachment = attachment
      const fileUrl: string = extAttachment.url ?? ''
      if (!fileUrl) {
        // Skip attachments without a valid URL
        return null
      }
      try {
        const response = await fetch(fileUrl)

        if (!response.ok) {
          throw new Error(`Failed to fetch file: ${fileUrl}`)
        }

        const blob = await response.blob()
        const fileName: string = extAttachment.fileName ?? 'attachment'
        const file = new File([blob], fileName, {
          type: extAttachment.mediaType ?? ''
        })
        return file
      } catch (error) {
        return null
      }
    })
  )
  // Filter out any null values (failed fetches or missing URLs)
  return files.filter((file): file is File => file !== null)
}
